import { useMemo, useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import MotionSection from "../common/MotionSection";
import { contactPageData } from "../../data/contactData";

const initialFormState = {
  fullName: "",
  email: "",
  phone: "",
  destination: "",
  studyLevel: "",
  message: ""
};

function ContactFormSection() {
  const { form } = contactPageData;
  const [formValues, setFormValues] = useState(initialFormState);
  const [submitState, setSubmitState] = useState("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  const nextStepNotes = useMemo(
    () => [
      "We review your profile and preferred destination.",
      "Our team contacts you with suitable counselling slots.",
      "You receive a clear action roadmap for your next step."
    ],
    []
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const endpoint = process.env.REACT_APP_GOOGLE_SHEETS_WEB_APP_URL?.trim();
    if (!endpoint) {
      setSubmitState("error");
      setSubmitMessage("Submission endpoint is missing. Please configure REACT_APP_GOOGLE_SHEETS_WEB_APP_URL.");
      return;
    }

    setSubmitState("submitting");
    setSubmitMessage("");

    const payload = {
      name: formValues.fullName.trim(),
      phone: formValues.phone.trim(),
      email: formValues.email.trim(),
      destination: formValues.destination.trim(),
      message: formValues.message.trim(),
      source: "nextera_abroad_contact_form"
    };

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain;charset=utf-8"
        },
        body: JSON.stringify(payload)
      });

      const rawBody = await response.text();
      let responseBody = null;

      if (rawBody) {
        try {
          responseBody = JSON.parse(rawBody);
        } catch (_error) {
          responseBody = null;
        }
      }

      if (!response.ok) {
        throw new Error(responseBody?.message || `Submission failed with status ${response.status}.`);
      }

      if (responseBody?.success === false || responseBody?.status === "error") {
        throw new Error(responseBody?.message || "Submission failed. Please try again.");
      }

      setSubmitState("success");
      setSubmitMessage(responseBody?.message || "Thanks for reaching out. Our team will connect with you soon.");
      setFormValues(initialFormState);
    } catch (error) {
      setSubmitState("error");
      setSubmitMessage(error?.message || "We could not submit your inquiry right now. Please try again.");
    }
  };

  const inputClass =
    "mt-2 w-full rounded-xl border border-midnight-navy/15 bg-white px-4 py-3 text-sm text-midnight-navy placeholder:text-slate-text/45 focus:border-royal-blue focus:outline-none focus:ring-2 focus:ring-royal-blue/25";

  return (
    <MotionSection id="contact-form" className="py-16 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-6">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div className="relative overflow-hidden rounded-[1.8rem] border border-midnight-navy/12 bg-gradient-to-br from-midnight-navy via-[#0f3b8d] to-royal-blue p-6 shadow-luxury md:p-8">
            <div className="absolute -right-16 top-0 h-52 w-52 rounded-full bg-white/14 blur-3xl" />
            <div className="absolute -left-20 bottom-0 h-48 w-48 rounded-full bg-warm-gold/24 blur-3xl" />

            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-glow/90">
                Premium Counselling Desk
              </p>
              <h2 className="mt-3 text-2xl leading-tight text-soft-white sm:text-3xl md:text-4xl">{form.title}</h2>
              <p className="mt-4 text-sm leading-relaxed text-sky-glow/85 sm:text-base">{form.subtitle}</p>

              <div className="mt-7 space-y-3">
                {nextStepNotes.map((note) => (
                  <div
                    key={note}
                    className="flex items-start gap-3 rounded-xl border border-white/20 bg-white/10 px-4 py-3"
                  >
                    <CheckCircle2 className="mt-0.5 h-4.5 w-4.5 flex-none text-warm-gold" aria-hidden="true" />
                    <p className="text-sm text-sky-glow/90">{note}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/20 bg-white/10 px-3 py-2">
                  <p className="text-lg font-semibold text-soft-white">24h</p>
                  <p className="text-[11px] uppercase tracking-[0.08em] text-sky-glow/80">Fast callback</p>
                </div>
                <div className="rounded-xl border border-white/20 bg-white/10 px-3 py-2">
                  <p className="text-lg font-semibold text-soft-white">1:1</p>
                  <p className="text-[11px] uppercase tracking-[0.08em] text-sky-glow/80">Personal plan</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[1.8rem] border border-midnight-navy/12 bg-white/94 p-6 shadow-luxury md:p-8">
            <p className="mb-4 inline-flex rounded-full border border-royal-blue/20 bg-royal-blue/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-royal-blue">
              Fill the form and we will reach out
            </p>
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="grid gap-5 md:grid-cols-2">
                <label className="block text-sm font-medium text-midnight-navy" htmlFor="fullName">
                  Full Name
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formValues.fullName}
                    onChange={handleChange}
                    className={inputClass}
                    required
                    autoComplete="name"
                  />
                </label>

                <label className="block text-sm font-medium text-midnight-navy" htmlFor="email">
                  Email Address
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formValues.email}
                    onChange={handleChange}
                    className={inputClass}
                    required
                    autoComplete="email"
                  />
                </label>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <label className="block text-sm font-medium text-midnight-navy" htmlFor="phone">
                  Phone Number
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formValues.phone}
                    onChange={handleChange}
                    className={inputClass}
                    required
                    autoComplete="tel"
                  />
                </label>

                <label className="block text-sm font-medium text-midnight-navy" htmlFor="destination">
                  Interested Destination
                  <select
                    id="destination"
                    name="destination"
                    value={formValues.destination}
                    onChange={handleChange}
                    className={inputClass}
                    required
                  >
                    <option value="">Select a destination</option>
                    {form.destinationOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <label className="block text-sm font-medium text-midnight-navy" htmlFor="studyLevel">
                  Study Level
                  <select
                    id="studyLevel"
                    name="studyLevel"
                    value={formValues.studyLevel}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">Select study level (optional)</option>
                    {form.studyLevelOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="block text-sm font-medium text-midnight-navy" htmlFor="message">
                Message
                <textarea
                  id="message"
                  name="message"
                  value={formValues.message}
                  onChange={handleChange}
                  className={`${inputClass} min-h-[130px] resize-y`}
                  placeholder="Tell us your goals, timeline, and any questions you have."
                  required
                />
              </label>

              <button
                type="submit"
                disabled={submitState === "submitting"}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-midnight-navy to-royal-blue px-6 py-3 text-sm font-semibold text-soft-white shadow-soft hover:from-[#0a2657] hover:to-[#0a58d6]"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {submitState === "submitting" ? "Sending..." : form.submitLabel}
              </button>
            </form>

            <p className="mt-4 text-xs text-slate-text/70" aria-live="polite">
              {submitState === "success" && submitMessage}
              {submitState === "error" && submitMessage}
              {submitState === "idle" &&
                "Share your details and we will reach out with personalized next steps."}
              {submitState === "submitting" && "Submitting your inquiry..."}
            </p>
          </div>
        </div>
      </div>
    </MotionSection>
  );
}

export default ContactFormSection;
